import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { fetchFinancings, homeExpenseQueryKeys } from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { formatShort } from "@project/ui";
import { FinancingTermProgressBlock } from "@apps/dashboard/components/financings/FinancingTermProgressBlock";

export default function FinancingsListPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  useSyncHouseFromParams(houseId);

  const listQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.financingsList(locale, houseId) : ["fin", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchFinancings(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.home}>{t("dashboardUi.mobile.homeLink")}</DashboardBackLink>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-headline text-headline-sm font-bold text-primary">
          {t("dashboardUi.financings.pageTitle")}
        </h1>
        <Button asChild variant="default">
          <Link to={dashboardPaths.houseFinancingNew(houseId)}>{t("dashboardUi.financings.newCta")}</Link>
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : listQuery.isError ? (
        <div className="mt-8">
          <ErrorState />
        </div>
      ) : !listQuery.data?.length ? (
        <div className="mt-10 rounded-2xl border border-outline-variant/30 bg-surface-container-low/40 p-8 text-center">
          <p className="font-headline text-title-md text-on-surface">{t("dashboardUi.financings.emptyTitle")}</p>
          <p className="mt-2 text-body-md text-on-surface-variant">{t("dashboardUi.financings.emptyHint")}</p>
          <Button asChild className="mt-6">
            <Link to={dashboardPaths.houseFinancingNew(houseId)}>{t("dashboardUi.financings.newCta")}</Link>
          </Button>
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {listQuery.data.map((row) => (
            <li key={row.id}>
              <Link
                to={dashboardPaths.houseFinancingDetail(houseId, row.id)}
                className="block rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-5 py-4 transition-colors hover:border-primary/40"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-headline text-title-lg font-bold leading-snug tracking-tight text-on-surface">
                      {row.title}
                    </p>
                    {row.lender ? (
                      <p className="mt-1 text-body-sm font-normal leading-normal text-on-surface-variant">
                        <span className="sr-only">{t("dashboardUi.financings.lenderLabel")}: </span>
                        {row.lender}
                      </p>
                    ) : null}
                  </div>
                  <p className="shrink-0 text-right text-label-md font-medium leading-snug text-on-surface-variant">
                    {t("dashboardUi.financings.listInstallment", {
                      amount: formatMinorAsCurrency(row.installmentAmountMinor, row.currency, locale),
                    })}
                  </p>
                </div>
                <p className="mt-3 text-body-sm text-on-surface-variant/90">
                  {formatShort(row.contractStartOn)} → {formatShort(row.endsOn)}
                </p>
                <FinancingTermProgressBlock row={row} className="mt-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
